import { useCallback, useEffect, useMemo, useState } from "react";
import {
  addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, format, isSameMonth, isToday, isSameDay,
} from "date-fns";
import { supabase } from "../../lib/supabase";
import { extractAndFormatTime } from "../../lib/timeUtils";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import type { Deal } from "../../lib/types";
import { daysInStage as dealDaysInStage } from "../../lib/dealAge";
import DealLink from "../DealLink";
import DealCallText from "../DealCallText";
import SchedulePreModal from "./actions/SchedulePreModal";
import ScheduleBuildModal from "./actions/ScheduleBuildModal";
import ScheduleAdjusterModal from "./actions/ScheduleAdjusterModal";
import DispatchQuestionPill from "./comms/DispatchQuestionPill";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";
import { addDaysISO, buildDaysIn, BUILD_SPAN_LOOKBACK_DAYS } from "../../lib/buildCalendar";
import { Link } from "react-router-dom";

/**
 * Schedule Tab — split layout matching Dispatch Board Hybrid Options artifact.
 * Left: mini calendar with day's events below.
 * Right: unscheduled queue with SLA-prioritized cards.
 */

type EventType = "adj" | "reinspAdj" | "pre" | "build";
type QueueType = "unassigned" | "pre_inspection" | "adjuster_meeting" | "build";
type Urgency = "overdue" | "today" | "week" | "none";

// Roles eligible to be assigned as the roof specialist (mirrors RoofMasterTable).
const RS_ROLES = new Set([
  "roof_specialist", "roof_team_manager", "qc", "roof_director",
  "director_of_operations", "team_lead", "asm", "restore_manager",
]);

interface RsCandidate { id: string; full_name: string; }

interface CalEvent {
  deal: Deal;
  type: EventType;
  time: string | null;
}

interface QueueRow {
  key: string;
  type: QueueType;
  deal: Deal;
  urgency: Urgency;
  label: string;
  rmName: string;
}

interface OpenPre {
  deal: Deal;
  scheduled: string | null; // pre_inspection_date if set
  rsName: string;
  urgency: Urgency;
  label: string;
}

const TYPE_LABEL: Record<EventType, string> = { adj: "ADJ", reinspAdj: "2ND", pre: "PRE", build: "BUILD" };
const TYPE_CLS: Record<EventType, string> = {
  adj: "bg-indigo-100 text-indigo-700",
  reinspAdj: "bg-purple-100 text-purple-700",
  pre: "bg-amber-100 text-amber-700",
  build: "bg-emerald-100 text-emerald-700",
};
const Q_TYPE_LABEL: Record<QueueType, string> = { unassigned: "RS", pre_inspection: "PRE", adjuster_meeting: "ADJ", build: "Build" };
const Q_TYPE_CLS: Record<QueueType, string> = {
  unassigned: "bg-rose-100 text-rose-700",
  pre_inspection: "bg-amber-100 text-amber-700",
  adjuster_meeting: "bg-indigo-100 text-indigo-700",
  build: "bg-emerald-100 text-emerald-700",
};
const SLA_DOT: Record<Urgency, string> = {
  overdue: "bg-red-600",
  today: "bg-amber-500",
  week: "bg-blue-500",
  none: "bg-slate-300",
};

const DEAL_COLS = "id,homeowner_name,homeowner_phone,property_street,property_city,property_state,property_zip,claim_number,stage,phase,owner_id,split_rm_id,days_in_stage,stage_entered_at,phase_entered_at,created_at,adjuster_meeting_date,pre_inspection_date,pre_inspection_assigned_to,rs_assignment_date,build_scheduled_date,build_end_date,ho_preferred_build_date,shingle_color,drip_edge_color,insurance_company,reinspection_adjuster_meeting_date,hover_link,scope_document_url,pre_inspection_data,pre_inspection_findings,pre_inspection_completed,upgrades,retail_profit,at_cost_retail,upgrades_description,upgrades_internal_note,retail_profit_description,retail_profit_internal_note,at_cost_retail_description,at_cost_retail_internal_note";

interface Props {
  onOpenPMBoard?: (dealId: string) => void;
}

export default function ScheduleTab({ onOpenPMBoard }: Props) {
  const [month, setMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [calEvents, setCalEvents] = useState<Record<string, CalEvent[]>>({});
  const [queueRows, setQueueRows] = useState<QueueRow[] | null>(null);
  const [openPres, setOpenPres] = useState<OpenPre[] | null>(null);
  const [, setMembers] = useState<Record<string, string>>({});
  const [rsCandidates, setRsCandidates] = useState<RsCandidate[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [qFilter, setQFilter] = useState<"all" | QueueType>("all");
  const [savingRsFor, setSavingRsFor] = useState<string | null>(null);

  // Modal state
  const [schedPreFor, setSchedPreFor] = useState<Deal | null>(null);
  const [schedBuildFor, setSchedBuildFor] = useState<Deal | null>(null);
  const [schedAdjFor, setSchedAdjFor] = useState<Deal | null>(null);

  // Roof Master section — desktop-only, default-collapsed

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  useRealtimeTable("deals", reload);

  // Inline RS assignment — writes the canonical assigned_rs_id, mirrors it to
  // pre_inspection_assigned_to (so the "Ask RS" dispatch question routes right),
  // and stamps rs_assignment_date. Does NOT advance the stage; once the date is
  // set the deal drops out of the Unassigned query and into the PRE query.
  // Mirrors RoofMasterTable.assignRs.
  const assignRs = useCallback(async (dealId: string, rsId: string) => {
    if (!rsId) return;
    setSavingRsFor(dealId);
    setError(null);
    try {
      const { error: upErr } = await supabase.from("deals").update({
        assigned_rs_id: rsId,
        pre_inspection_assigned_to: rsId,
        rs_assignment_date: new Date().toISOString(),
      }).eq("id", dealId);
      if (upErr) throw upErr;
      reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingRsFor(null);
    }
  }, [reload]);

  // Load calendar events for the visible grid + unscheduled queue.
  // Visible grid spans from startOfWeek(monthStart) to endOfWeek(monthEnd),
  // which can run a few days into the previous/next month — those days are
  // tappable and the weekly strip can sit on a Mon that's still in the prior
  // month, so we have to load events for the full visible range, not just
  // the calendar month. Matches the 3-month range the Company Schedule
  // (CalendarTab) loads, so the two views never disagree.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setError(null);
      const rangeStart = format(startOfWeek(startOfMonth(month), { weekStartsOn: 1 }), "yyyy-MM-dd");
      const rangeEnd = format(endOfWeek(endOfMonth(month), { weekStartsOn: 1 }), "yyyy-MM-dd");
      const today = new Date();

      const [
        { data: adjData, error: e1 },
        { data: preData, error: e2 },
        { data: buildData, error: e3 },
        { data: reinspData, error: e4 },
        // Unscheduled queries
        { data: unschedUnassigned, error: e8 },
        { data: unschedPre, error: e5 },
        { data: unschedAdj, error: e6 },
        { data: unschedBuild, error: e7 },
        // Every open pre-inspection: deals currently in the PRE stage that
        // haven't been completed yet — covers both "needs a date" and
        // "scheduled but not done" (scheduling only stamps pre_inspection_date,
        // it doesn't leave the stage; the PRE Done modal advances to
        // adjuster_meeting_ready).
        { data: openPreData, error: e9 },
        { data: mems },
      ] = await Promise.all([
        supabase.from("deals").select(DEAL_COLS)
          .gte("adjuster_meeting_date", `${rangeStart}T00:00:00Z`).lte("adjuster_meeting_date", `${rangeEnd}T23:59:59Z`),
        supabase.from("deals").select(DEAL_COLS)
          .gte("pre_inspection_date", `${rangeStart}T00:00:00Z`).lte("pre_inspection_date", `${rangeEnd}T23:59:59Z`),
        // Builds that started earlier still show on each later day they run (Oct 26, 2026).
        supabase.from("deals").select(DEAL_COLS)
          .gte("build_scheduled_date", addDaysISO(rangeStart, -BUILD_SPAN_LOOKBACK_DAYS)).lte("build_scheduled_date", rangeEnd),
        supabase.from("deals").select(DEAL_COLS)
          .gte("reinspection_adjuster_meeting_date", `${rangeStart}T00:00:00Z`).lte("reinspection_adjuster_meeting_date", `${rangeEnd}T23:59:59Z`),
        // Unassigned: in the assign_roof_specialist stage with no RS assigned yet.
        supabase.from("deals").select(DEAL_COLS)
          .eq("stage", "assign_roof_specialist").is("rs_assignment_date", null),
        supabase.from("deals").select(DEAL_COLS)
          .eq("phase", "claims").is("pre_inspection_date", null).not("rs_assignment_date", "is", null)
          .neq("stage", "claims_denied"),
        supabase.from("deals").select(DEAL_COLS)
          .eq("phase", "claims").is("adjuster_meeting_date", null).not("pre_inspection_date", "is", null)
          .neq("stage", "claims_denied"),
        supabase.from("deals").select(DEAL_COLS)
          .eq("stage", "schedule_build").is("build_scheduled_date", null),
        supabase.from("deals").select(DEAL_COLS)
          .eq("stage", "pre_inspection_ready"),
        supabase.from("team_roster").select("id,full_name,role,is_active").eq("is_active", true),
      ]);

      const err = e1 ?? e2 ?? e3 ?? e4 ?? e5 ?? e6 ?? e7 ?? e8 ?? e9;
      if (err) { if (!cancelled) setError(err.message); return; }

      const memberMap: Record<string, string> = {};
      (mems ?? []).forEach((m) => { memberMap[m.id] = m.full_name; });
      const candidates: RsCandidate[] = (mems ?? [])
        .filter((m: any) => m.is_active && RS_ROLES.has(m.role))
        .map((m: any) => ({ id: m.id, full_name: m.full_name }))
        .sort((a: RsCandidate, b: RsCandidate) => a.full_name.localeCompare(b.full_name));

      // Build calendar events by date key
      const events: Record<string, CalEvent[]> = {};
      function addEvent(d: any, type: EventType, dateField: string) {
        if (!d[dateField]) return;
        const key = d[dateField].substring(0, 10);
        if (!events[key]) events[key] = [];
        events[key].push({ deal: d as Deal, type, time: extractAndFormatTime(d[dateField]) });
      }
      (adjData ?? []).forEach((d) => addEvent(d, "adj", "adjuster_meeting_date"));
      (preData ?? []).forEach((d) => addEvent(d, "pre", "pre_inspection_date"));
      // A multi-day build sits on each of its days with "Day n of N".
      (buildData ?? []).forEach((d) => {
        for (const bd of buildDaysIn(d.build_scheduled_date, d.build_end_date, rangeStart, rangeEnd)) {
          (events[bd.iso] ??= []).push({ deal: d as Deal, type: "build", time: bd.of > 1 ? `Day ${bd.day} of ${bd.of}` : extractAndFormatTime(d.build_scheduled_date) });
        }
      });
      (reinspData ?? []).forEach((d) => addEvent(d, "reinspAdj", "reinspection_adjuster_meeting_date"));

      // Build unscheduled queue. rmName combines owner + split if both exist
      // so dispatch can see who would receive an "Ask" before clicking.
      const formatRmNames = (ownerId: string | null | undefined, splitId: string | null | undefined) => {
        const owner = ownerId ? memberMap[ownerId]?.split(" ")[0] : null;
        const split = splitId && splitId !== ownerId ? memberMap[splitId]?.split(" ")[0] : null;
        if (owner && split) return `${owner} + ${split}`;
        return owner ?? "—";
      };

      const queue: QueueRow[] = [];
      (unschedUnassigned ?? []).forEach((d) => {
        const daysInStage = dealDaysInStage(d);
        const urgency: Urgency = daysInStage > 3 ? "overdue" : daysInStage === 3 ? "today" : daysInStage >= 2 ? "week" : "none";
        queue.push({
          key: `unassigned-${d.id}`, type: "unassigned", deal: d as Deal, urgency,
          label: `Needs RS · ${daysInStage}d in stage`,
          rmName: formatRmNames(d.owner_id, (d as Deal).split_rm_id),
        });
      });
      (unschedPre ?? []).forEach((d) => {
        const assigned = d.rs_assignment_date ? new Date(d.rs_assignment_date) : null;
        if (!assigned) return;
        const daysSince = Math.floor((today.getTime() - assigned.getTime()) / 86_400_000);
        const urgency: Urgency = daysSince > 3 ? "overdue" : daysSince === 3 ? "today" : daysSince >= 2 ? "week" : "none";
        queue.push({
          key: `pre-${d.id}`, type: "pre_inspection", deal: d as Deal, urgency,
          label: `RS assigned ${daysSince}d ago` + (daysSince > 3 ? ` · ${daysSince - 3}d overdue` : ""),
          rmName: formatRmNames(d.owner_id, (d as Deal).split_rm_id),
        });
      });
      (unschedAdj ?? []).forEach((d) => {
        const daysInStage = dealDaysInStage(d);
        const urgency: Urgency = daysInStage > 5 ? "overdue" : daysInStage >= 3 ? "week" : "none";
        // Skip deals that already have a meeting on the calendar or moved past to claims.
        if (d.adjuster_meeting_date != null || d.stage === "waiting_on_claims") return;
        queue.push({
          key: `adj-${d.id}`, type: "adjuster_meeting", deal: d as Deal, urgency,
          label: `PRE done · ${daysInStage}d in stage`,
          rmName: formatRmNames(d.owner_id, (d as Deal).split_rm_id),
        });
      });
      (unschedBuild ?? []).forEach((d) => {
        const pref = d.ho_preferred_build_date ? new Date(d.ho_preferred_build_date) : null;
        let urgency: Urgency = "none";
        let label = `${dealDaysInStage(d)}d in stage`;
        if (pref) {
          const daysUntil = Math.floor((pref.getTime() - today.getTime()) / 86_400_000);
          urgency = daysUntil < 0 ? "overdue" : daysUntil <= 2 ? "today" : daysUntil <= 7 ? "week" : "none";
          label = `Preferred: ${format(pref, "MMM d")}` + (daysUntil < 0 ? ` · ${Math.abs(daysUntil)}d past` : "");
        } else if (dealDaysInStage(d) > 7) {
          urgency = "overdue"; // legacy block — doesn't matter, keeping intact
        }
        queue.push({
          key: `build-${d.id}`, type: "build", deal: d as Deal, urgency, label,
          rmName: formatRmNames(d.owner_id, (d as Deal).split_rm_id),
        });
      });

      // Sort: overdue first, then today, week, none
      const urgencyOrder: Record<Urgency, number> = { overdue: 0, today: 1, week: 2, none: 3 };
      queue.sort((a, b) => urgencyOrder[a.urgency] - urgencyOrder[b.urgency]);

      // Build the "open pre-inspections" list (every deal still in the PRE
      // stage). PRE SLA = 3 days from RS assignment (mirrors SchedulePreModal).
      const pres: OpenPre[] = [];
      (openPreData ?? []).forEach((d) => {
        if (d.pre_inspection_completed === true) return; // defensive
        const rsName =
          (d.pre_inspection_assigned_to && memberMap[d.pre_inspection_assigned_to]?.split(" ")[0]) ||
          "Unassigned";
        if (d.pre_inspection_date) {
          // Scheduled but not yet completed.
          pres.push({
            deal: d as Deal,
            scheduled: d.pre_inspection_date,
            rsName,
            urgency: "none",
            label: `Scheduled ${format(new Date(d.pre_inspection_date.substring(0, 10) + "T12:00:00"), "MMM d")}`,
          });
        } else {
          // Needs a date — SLA clock runs from RS assignment.
          const assigned = d.rs_assignment_date ? new Date(d.rs_assignment_date) : null;
          const daysSince = assigned
            ? Math.floor((today.getTime() - assigned.getTime()) / 86_400_000)
            : 0;
          const urgency: Urgency =
            daysSince > 3 ? "overdue" : daysSince === 3 ? "today" : daysSince >= 2 ? "week" : "none";
          pres.push({
            deal: d as Deal,
            scheduled: null,
            rsName,
            urgency,
            label:
              `Needs date · RS assigned ${daysSince}d ago` +
              (daysSince > 3 ? ` · ${daysSince - 3}d overdue` : ""),
          });
        }
      });
      // Needs-a-date first (overdue→none), then the scheduled ones by date.
      pres.sort((a, b) => {
        if (!a.scheduled && b.scheduled) return -1;
        if (a.scheduled && !b.scheduled) return 1;
        if (!a.scheduled && !b.scheduled) return urgencyOrder[a.urgency] - urgencyOrder[b.urgency];
        return (a.scheduled ?? "").localeCompare(b.scheduled ?? "");
      });

      if (!cancelled) {
        setCalEvents(events);
        setQueueRows(queue);
        setOpenPres(pres);
        setMembers(memberMap);
        setRsCandidates(candidates);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [month, reloadKey]);

  // Calendar grid
  const calDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
    const days: Date[] = [];
    let d = start;
    while (d <= end) { days.push(d); d = addDays(d, 1); }
    return days;
  }, [month]);

  // Selected day events
  const dayKey = format(selectedDay, "yyyy-MM-dd");
  const dayEvents = calEvents[dayKey] ?? [];

  // Queue counts
  const qCounts = useMemo(() => {
    if (!queueRows) return { all: 0, unassigned: 0, pre_inspection: 0, adjuster_meeting: 0, build: 0, overdue: 0 };
    return {
      all: queueRows.length,
      unassigned: queueRows.filter((r) => r.type === "unassigned").length,
      pre_inspection: queueRows.filter((r) => r.type === "pre_inspection").length,
      adjuster_meeting: queueRows.filter((r) => r.type === "adjuster_meeting").length,
      build: queueRows.filter((r) => r.type === "build").length,
      overdue: queueRows.filter((r) => r.urgency === "overdue").length,
    };
  }, [queueRows]);

  const filteredQueue = useMemo(() => {
    if (!queueRows) return [];
    if (qFilter === "all") return queueRows;
    return queueRows.filter((r) => r.type === qFilter);
  }, [queueRows, qFilter]);

  if (error)
    return <div className="bg-red-50 text-red-700 p-4 rounded">Couldn't load schedule — {error}</div>;

  return (
    <>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {/* ── LEFT: Calendar + Day Events ── */}
      <div className="bg-white border border-slate-200 rounded-lg p-3.5">
        {/* Month nav */}
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => setMonth(subMonths(month, 1))} className="px-2 py-1 border border-slate-200 rounded text-[12px] hover:bg-slate-50">◀</button>
          <span className="font-bold text-[14px] text-slate-900">{format(month, "MMMM yyyy")}</span>
          <button onClick={() => setMonth(addMonths(month, 1))} className="px-2 py-1 border border-slate-200 rounded text-[12px] hover:bg-slate-50">▶</button>
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-0.5 text-center text-[10px]">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div key={d} className="font-bold text-slate-500 py-1">{d}</div>
          ))}
          {calDays.map((day, i) => {
            const dk = format(day, "yyyy-MM-dd");
            const hasEvents = !!calEvents[dk];
            const today = isToday(day);
            const selected = isSameDay(day, selectedDay) && !today;
            const inMonth = isSameMonth(day, month);
            return (
              <button
                key={i}
                onClick={() => setSelectedDay(day)}
                className={
                  "relative py-1.5 rounded-full text-[11px] transition " +
                  (!inMonth ? "text-slate-300 " : "") +
                  (today ? "bg-teal-500 text-white font-extrabold " : "") +
                  (selected ? "bg-blue-100 text-blue-700 font-bold " : "") +
                  (!today && !selected && inMonth ? "hover:bg-teal-50 " : "")
                }
              >
                {format(day, "d")}
                {hasEvents && (
                  <span className={"absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full " + (today ? "bg-white" : "bg-teal-500")} />
                )}
              </button>
            );
          })}
        </div>

        {/* ── Weekly view with appointment listings ── */}
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="font-bold text-[11px] text-slate-700 mb-2">
            📆 Week of {format(startOfWeek(selectedDay, { weekStartsOn: 1 }), "MMM d")} – {format(endOfWeek(selectedDay, { weekStartsOn: 1 }), "MMM d")}
          </div>
          <div className="grid grid-cols-7 gap-1 mb-3">
            {Array.from({ length: 7 }).map((_, i) => {
              const wd = addDays(startOfWeek(selectedDay, { weekStartsOn: 1 }), i);
              const wdk = format(wd, "yyyy-MM-dd");
              const wdEvents = calEvents[wdk] ?? [];
              const isSel = isSameDay(wd, selectedDay);
              const wdToday = isToday(wd);
              return (
                <button
                  key={i}
                  onClick={() => setSelectedDay(wd)}
                  className={
                    "rounded-lg p-1.5 text-left transition border min-h-[80px] flex flex-col " +
                    (isSel
                      ? "border-teal-500 bg-teal-50"
                      : wdToday
                      ? "border-teal-300 bg-teal-50/50"
                      : "border-slate-100 hover:bg-slate-50")
                  }
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[9px] font-semibold text-slate-400">
                      {format(wd, "EEE")}
                    </span>
                    <span className={
                      "text-[12px] font-bold " +
                      (isSel ? "text-teal-600" : wdToday ? "text-teal-500" : "text-slate-700")
                    }>
                      {format(wd, "d")}
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5 flex-1">
                    {wdEvents.slice(0, 4).map((ev, j) => (
                      <div key={j} className="flex items-center gap-1 min-w-0">
                        <span className={"shrink-0 px-1 rounded text-[7px] font-bold leading-tight " + TYPE_CLS[ev.type]}>
                          {TYPE_LABEL[ev.type]}
                        </span>
                        <span className="text-[8px] text-slate-600 truncate">
                          {(ev.deal.homeowner_name ?? "").split(" ").pop()}
                        </span>
                      </div>
                    ))}
                    {wdEvents.length > 4 && (
                      <span className="text-[7px] text-slate-400 font-semibold">+{wdEvents.length - 4} more</span>
                    )}
                    {wdEvents.length === 0 && (
                      <span className="text-[8px] text-slate-300 italic mt-1">—</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Selected day's events ── */}
        <div className="pt-2 border-t border-slate-100">
          <div className="font-bold text-[11px] text-slate-700 mb-2">
            📅 {format(selectedDay, "EEEE, MMM d")} — {dayEvents.length} stop{dayEvents.length !== 1 ? "s" : ""}
          </div>
          {dayEvents.length === 0 ? (
            <div className="text-center text-slate-400 text-[11px] py-3">No stops on this day</div>
          ) : (
            <div className="space-y-1">
              {dayEvents.map((ev, i) => (
                <div key={i} className="flex items-center gap-2 py-1.5 text-[11px]">
                  <span className={"px-1.5 py-0.5 rounded text-[9px] font-bold " + TYPE_CLS[ev.type]}>{TYPE_LABEL[ev.type]}</span>
                  <span className="font-semibold text-slate-900">
                    <DealLink id={ev.deal.id}>{ev.deal.homeowner_name ?? "(no name)"}</DealLink>
                  </span>
                  <span className="text-slate-400 ml-auto">{ev.time ?? "All Day"}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Open pre-inspections (everything still in the PRE stage) ── */}
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-bold text-[11px] text-slate-700">
              🔍 Open Pre-Inspections
            </span>
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-lg bg-amber-100 text-amber-700">
              {openPres?.length ?? 0}
            </span>
          </div>
          {!openPres ? (
            <div className="text-slate-400 text-[11px] text-center py-3">Loading…</div>
          ) : openPres.length === 0 ? (
            <div className="text-center text-slate-400 text-[11px] py-3">No open pre-inspections</div>
          ) : (
            <div className="max-h-[260px] overflow-y-auto space-y-1.5">
              {openPres.map((p) => (
                <div
                  key={p.deal.id}
                  className={
                    "bg-white rounded-lg px-3 py-2 border-l-[3px] text-[11px] transition hover:shadow-sm " +
                    (p.urgency === "overdue" ? "border-l-red-600" :
                     p.urgency === "today" ? "border-l-amber-500" :
                     p.urgency === "week" ? "border-l-blue-500" :
                     p.scheduled ? "border-l-emerald-400" : "border-l-slate-200")
                  }
                >
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${SLA_DOT[p.urgency]}`} />
                    <span className="font-bold text-slate-900 text-[12px]">
                      <DealLink id={p.deal.id}>{p.deal.homeowner_name ?? "(no name)"}</DealLink>
                    </span>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">PRE</span>
                    <span className="ml-auto text-[10px] text-slate-500">{p.rsName}</span>
                  </div>
                  <div className="text-slate-500 text-[10px] mt-0.5">
                    {isAddressLinkable(p.deal) ? (
                      <a href={mapsHref(p.deal)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="hover:underline hover:text-slate-700">{[p.deal.property_street, p.deal.property_city].filter(Boolean).join(", ")}</a>
                    ) : (
                      [p.deal.property_street, p.deal.property_city].filter(Boolean).join(", ")
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-slate-500 text-[10px]">{p.label}</span>
                    {!p.scheduled && (
                      <button
                        onClick={() => setSchedPreFor(p.deal)}
                        className="ml-auto px-2.5 py-1 rounded-md bg-teal-500 text-white text-[10px] font-semibold hover:bg-teal-600 transition"
                      >
                        Schedule PRE →
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── RIGHT: Unscheduled Queue ── */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3.5">
        <div className="flex items-center gap-2 mb-2">
          <span className="font-bold text-[13px] text-slate-900">⏳ Unscheduled Queue</span>
          {qCounts.overdue > 0 && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-lg bg-red-100 text-red-700">
              {qCounts.overdue} overdue
            </span>
          )}
        </div>

        {/* Filter pills */}
        <div className="flex gap-1 flex-wrap mb-3">
          {([
            { key: "all" as const, label: `All (${qCounts.all})` },
            { key: "unassigned" as const, label: `Unassigned (${qCounts.unassigned})` },
            { key: "pre_inspection" as const, label: `PRE (${qCounts.pre_inspection})` },
            { key: "adjuster_meeting" as const, label: `Adj (${qCounts.adjuster_meeting})` },
            { key: "build" as const, label: `Build (${qCounts.build})` },
          ]).map((f) => (
            <button
              key={f.key}
              onClick={() => setQFilter(f.key)}
              className={
                "px-2.5 py-1 rounded-xl text-[10px] font-semibold border transition " +
                (qFilter === f.key
                  ? "bg-teal-500 text-white border-teal-500"
                  : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50")
              }
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Queue cards */}
        <div className="max-h-[420px] overflow-y-auto space-y-1.5">
          {!queueRows ? (
            <div className="text-slate-400 text-[11px] text-center py-4">Loading…</div>
          ) : filteredQueue.length === 0 ? (
            <div className="text-center py-6 text-slate-400 text-[11px]">All caught up!</div>
          ) : (
            filteredQueue.map((row) => (
              <div
                key={row.key}
                className={
                  "bg-white rounded-lg px-3 py-2.5 border-l-[3px] text-[11px] transition hover:shadow-sm " +
                  (row.urgency === "overdue" ? "border-l-red-600" :
                   row.urgency === "today" ? "border-l-amber-500" :
                   row.urgency === "week" ? "border-l-blue-500" :
                   "border-l-slate-200")
                }
              >
                <div className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${SLA_DOT[row.urgency]}`} />
                  <span className="font-bold text-slate-900 text-[12px]">
                    <DealLink id={row.deal.id}>{row.deal.homeowner_name ?? "(no name)"}</DealLink>
                  </span>
                  <span className={"text-[9px] font-bold px-1.5 py-0.5 rounded " + Q_TYPE_CLS[row.type]}>
                    {Q_TYPE_LABEL[row.type]}
                  </span>
                </div>
                <div className="text-slate-500 text-[10px] mt-0.5">
                  {isAddressLinkable(row.deal) ? (
                    <a href={mapsHref(row.deal)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="hover:underline hover:text-slate-700">{[row.deal.property_street, row.deal.property_city].filter(Boolean).join(", ")}</a>
                  ) : (
                    [row.deal.property_street, row.deal.property_city].filter(Boolean).join(", ")
                  )} · {row.rmName}
                </div>
                <div className="text-slate-500 text-[10px] mt-0.5">{row.label}</div>
                <div className="flex items-center gap-1.5 mt-1.5">
                  {row.type === "unassigned" ? (
                    <select
                      defaultValue=""
                      disabled={savingRsFor === row.deal.id}
                      onChange={(e) => assignRs(row.deal.id, e.target.value)}
                      className="px-2 py-1 rounded-md bg-rose-500 text-white text-[10px] font-semibold border-0 hover:bg-rose-600 transition disabled:opacity-50 cursor-pointer"
                    >
                      <option value="" disabled>{savingRsFor === row.deal.id ? "Assigning…" : "Assign RS →"}</option>
                      {rsCandidates.map((c) => (
                        <option key={c.id} value={c.id} className="bg-white text-slate-900">{c.full_name}</option>
                      ))}
                    </select>
                  ) : (
                    <button
                      onClick={() => {
                        if (row.type === "pre_inspection") setSchedPreFor(row.deal);
                        else if (row.type === "build") setSchedBuildFor(row.deal);
                        else setSchedAdjFor(row.deal);
                      }}
                      className="px-2.5 py-1 rounded-md bg-teal-500 text-white text-[10px] font-semibold hover:bg-teal-600 transition"
                    >
                      {row.type === "build" ? "Schedule Build →" : row.type === "adjuster_meeting" ? "Schedule Adj →" : "Schedule PRE →"}
                    </button>
                  )}
                  <div className="ml-auto flex items-center gap-1.5">
                    <DispatchQuestionPill
                      deal={{
                        id: row.deal.id,
                        homeowner_name: row.deal.homeowner_name,
                        property_street: row.deal.property_street,
                        owner_id: row.deal.owner_id,
                        split_rm_id: (row.deal as Deal).split_rm_id ?? null,
                        pre_inspection_assigned_to: row.deal.pre_inspection_assigned_to ?? null,
                        phase: row.deal.phase ?? null,
                      }}
                    />
                    <DealCallText dealId={row.deal.id} homeownerName={row.deal.homeowner_name} homeownerPhone={row.deal.homeowner_phone} compact />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Scheduling modals */}
      {schedPreFor && (
        <SchedulePreModal open deal={schedPreFor} onClose={() => setSchedPreFor(null)} onCompleted={reload} />
      )}
      {schedBuildFor && (
        <ScheduleBuildModal open deal={schedBuildFor} onClose={() => setSchedBuildFor(null)} onCompleted={() => { reload(); if (onOpenPMBoard) onOpenPMBoard(schedBuildFor.id); }} />
      )}
      {schedAdjFor && (
        <ScheduleAdjusterModal open deal={schedAdjFor} onClose={() => setSchedAdjFor(null)} onCompleted={reload} />
      )}
    </div>

    {/* ── Roof Master moved to its own page (/roof-master) Jul 30, 2026 per
           Robert — this is just the doorway so the old habit still lands. ── */}
    <div className="mt-4">
      <Link
        to="/roof-master"
        className="w-full flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition text-left"
      >
        <span className="font-semibold text-bardin-ink">📋 Roof Master</span>
        <span className="text-xs text-slate-500">— now on its own page · open full-screen →</span>
      </Link>
    </div>
    </>
  );
}
